import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Alert,
  Container,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HotelIcon from '@mui/icons-material/Hotel';
import SpaIcon from '@mui/icons-material/Spa';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import PaidIcon from '@mui/icons-material/Paid';
import { Divider } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { parse, addDays, setHours, setMinutes, setSeconds } from 'date-fns';
import { getOrders } from '../../services/orderService';
import { useAuth } from '../../contexts/AuthContext';
import { useBusinessDate } from '../../hooks/useBusinessDate';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { CastOrderView, toCastOrderView, extractOrders } from './castOrderUtils';

/**
 * キャストユーザー専用のオーダー一覧画面。
 * スマホで見やすいように、予約時間・予約タイプ・コース・ホテルのみをカードにまとめて表示する。
 * カードをタップすると、そのオーダーのキャスト専用編集画面へ遷移する。
 */
const CastOrderList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { businessDate, isLoading: isDateLoading, formatBusinessDate, businessHours } = useBusinessDate();
  const { loading: settingsLoading } = useSystemSettings();

  const [orders, setOrders] = useState<CastOrderView[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // 表示対象日（初期値は営業日。ユーザーが日付選択で変更できる）
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  // 予約時間の並び順（asc=早い順 / desc=遅い順）
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const fetchOrders = useCallback(async (date: Date) => {
    if (!user || !businessHours) return;

    try {
      setLoading(true);
      setError(null);

      const startTime = parse(businessHours.start, 'HH:mm', new Date());
      const endTime = parse(businessHours.end, 'HH:mm', new Date());

      const searchStart = setSeconds(setMinutes(setHours(date, startTime.getHours()), startTime.getMinutes()), 0);
      let searchEnd;
      if (endTime < startTime) {
        const nextDay = addDays(date, 1);
        searchEnd = setSeconds(setMinutes(setHours(nextDay, endTime.getHours()), endTime.getMinutes()), 0);
      } else {
        searchEnd = setSeconds(setMinutes(setHours(date, endTime.getHours()), endTime.getMinutes()), 0);
      }

      // user_id/cast_id は明示指定しない。
      // バックエンドがログインユーザー(CAST)のロールに応じて自分担当のオーダーだけを返す。
      const response = await getOrders({
        start_date: searchStart.toISOString(),
        end_date: searchEnd.toISOString(),
      });

      const raw = extractOrders(response);
      setOrders(raw.map(toCastOrderView));
    } catch (err) {
      console.error('オーダー取得に失敗しました:', err);
      setError('オーダーデータの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [user, businessHours]);

  // 営業日が確定したら、未選択のうちは選択日の初期値として設定する
  useEffect(() => {
    if (businessDate && !selectedDate) {
      setSelectedDate(businessDate);
    }
  }, [businessDate, selectedDate]);

  useEffect(() => {
    const init = async () => {
      const targetDate = selectedDate || businessDate;
      if (user && targetDate && !settingsLoading && !isDateLoading) {
        await fetchOrders(targetDate);
      } else if (!settingsLoading && !isDateLoading) {
        setLoading(false);
      }
    };
    init();
  }, [user, selectedDate, businessDate, settingsLoading, isDateLoading, fetchOrders]);

  // 予約時間でソートしたオーダー一覧
  const sortedOrders = useMemo(() => {
    const list = [...orders];
    list.sort((a, b) =>
      sortOrder === 'asc'
        ? a.reservationSortKey - b.reservationSortKey
        : b.reservationSortKey - a.reservationSortKey
    );
    return list;
  }, [orders, sortOrder]);

  const handleOpenOrder = (id: string | number) => {
    navigate(`/orders/${id}`);
  };

  // 初回（設定/営業日が未確定）のみ全画面スピナー。日付切替時はヘッダーを残す。
  if (settingsLoading || isDateLoading) {
    return (
      <DashboardLayout title="オーダー一覧">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="オーダー一覧">
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        オーダー一覧
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        営業日（基準）: {formatBusinessDate(businessDate)}
      </Typography>

      {/* 日付選択と並べ替え */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
          <DatePicker
            label="表示する日付"
            value={selectedDate}
            onChange={(newValue) => newValue && setSelectedDate(newValue)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </LocalizationProvider>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={sortOrder}
          onChange={(_e, val) => val && setSortOrder(val)}
        >
          <ToggleButton value="asc">予約が早い順</ToggleButton>
          <ToggleButton value="desc">予約が遅い順</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Box>
      ) : sortedOrders.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          選択した日付のオーダーはありません
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {sortedOrders.map((order) => (
            <Card
              key={order.id}
              variant="outlined"
              sx={order.isCompleted ? { borderColor: 'success.main', bgcolor: 'success.50' } : undefined}
            >
              <CardActionArea onClick={() => handleOpenOrder(order.id)}>
                <CardContent sx={{ py: 1.5 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <AccessTimeIcon fontSize="small" color="action" />
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {order.reservationLabel}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {order.isCompleted && (
                        <Chip label="完了" size="small" color="success" />
                      )}
                      <Chip label={order.orderTypeLabel} size="small" color="primary" variant="outlined" />
                    </Stack>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <SpaIcon fontSize="small" color="action" />
                    <Typography variant="body1">{order.courseLabel}</Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <HotelIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {order.hotelLabel}
                    </Typography>
                  </Stack>

                  {/* 完了オーダーは入室/退出/キャストバックを表示 */}
                  {order.isCompleted && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
                        {order.entryTimeLabel && (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <LoginIcon fontSize="small" color="success" />
                            <Typography variant="body2">入室 {order.entryTimeLabel}</Typography>
                          </Stack>
                        )}
                        {order.exitTimeLabel && (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <LogoutIcon fontSize="small" color="action" />
                            <Typography variant="body2">退出 {order.exitTimeLabel}</Typography>
                          </Stack>
                        )}
                        {order.castbackLabel && (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <PaidIcon fontSize="small" color="warning" />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              バック {order.castbackLabel}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}
    </Container>
    </DashboardLayout>
  );
};

export default CastOrderList;
